import React, { useEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import { Dialog } from "primereact/dialog";
import IdApi from "../service/IdApi";
import TokenService from "../utils/TokenService";
import LocalStorageService from "../utils/LocalStorageService";
import { useAppContext } from "../context/appContext";

const UnclaimedProfileSwitchDialog = React.memo(function UnclaimedProfileSwitchDialog({
    visible,
    listeningForMessages,
}) {
    return (
        <Dialog
            visible={visible}
            style={{ width: "225px" }}
            header="Switching Accounts"
            modal
            closable={false}
        >
            <div className="flex flex-column justify-content-center w-full" style={{ height: "75px" }}>
                <div className="text-lg text-center">
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>
                </div>
                {listeningForMessages && (
                    <>
                        <iframe
                            src={process.env.REACT_APP_NETWORK_SITE + "/logout"}
                            style={{ width: 1, height: 1, border: "none" }}
                            title="Network logout"
                        />
                        <iframe
                            src={process.env.REACT_APP_PROVIDER_SITE + "/logout"}
                            style={{ width: 1, height: 1, border: "none" }}
                            title="Provider logout"
                        />
                    </>
                )}
            </div>
        </Dialog>
    );
});

/**
 * Reusable flow for switching to an "unclaimed" provider profile:
 * - Call admin switch endpoint (sets tokens)
 * - While switching, load logout endpoints in hidden iframes to clear caches in other sites
 * - Wait for logout completion messages (or fallback after a short timeout) before redirecting
 */
export const useUnclaimedProfileSwitchDialog = () => {
    const { displayAlert } = useAppContext();
    const fallbackTimeoutRef = useRef(null);
    const [switchingProvidersInProgress, setSwitchingProvidersInProgress] = useState(false);
    const [listeningForMessages, setListeningForMessages] = useState(false);
    const [loggedOutNetworkUi, setLoggedOutNetworkUi] = useState(false);
    const [loggedOutProviderUi, setLoggedOutProviderUi] = useState(false);
    const [loggedOutIdUi, setLoggedOutIdUi] = useState(false);
    const [loggedInToNewProvider, setLoggedInToNewProvider] = useState(false);

    const switchUnclaimedCompanyApi = useMutation(async (companyId) => {
        let body = { providerId: companyId };
        const response = await IdApi.post("admin/switch-unclaimed-provider", body);
        return response.data;
    });

    useEffect(() => {
        if (!switchingProvidersInProgress) {
            return;
        }

        const handleMessage = (event) => {
            if (event.data && event.data.type === "wallistNetworkUi:providerLogoutComplete") {
                setLoggedOutNetworkUi(true);
            }
            if (event.data && event.data.type === "wallistProviderUi:providerLogoutComplete") {
                setLoggedOutProviderUi(true);
            }
        };

        window.addEventListener("message", handleMessage);
        setListeningForMessages(true);

        return () => {
            window.removeEventListener("message", handleMessage);
            setListeningForMessages(false);
        };
    }, [switchingProvidersInProgress]);

    useEffect(() => {
        if (!switchingProvidersInProgress) {
            return;
        }

        // Wait until both remote sites logged out + our local cache clear + the new provider login token swap.
        if (loggedOutNetworkUi && loggedOutProviderUi && loggedOutIdUi && loggedInToNewProvider) {
            if (TokenService.getProviderSubdomain()) {
                window.location.href = TokenService.getProviderAppUrl();
            }
        }
    }, [loggedOutNetworkUi, loggedOutProviderUi, loggedOutIdUi, loggedInToNewProvider, switchingProvidersInProgress]);

    useEffect(() => {
        if (!loggedInToNewProvider) {
            return;
        }

        fallbackTimeoutRef.current = window.setTimeout(() => {
            if (TokenService.getProviderSubdomain()) {
                window.location.href = TokenService.getProviderAppUrl();
            }
        }, 5000);

        return () => {
            if (fallbackTimeoutRef.current) {
                window.clearTimeout(fallbackTimeoutRef.current);
                fallbackTimeoutRef.current = null;
            }
        };
    }, [loggedInToNewProvider]);

    const switchUnclaimedProfile = async (companyId) => {
        setLoggedOutNetworkUi(false);
        setLoggedOutProviderUi(false);
        setLoggedOutIdUi(false);
        setLoggedInToNewProvider(false);
        setSwitchingProvidersInProgress(true);

        await LocalStorageService.logoutProvider();
        setLoggedOutIdUi(true);
        switchUnclaimedCompanyApi.mutate(companyId, {
            onError: () => {
                displayAlert("error", "Unexpected Error", "Please try again later");
                setSwitchingProvidersInProgress(false);
            },
            onSuccess: (data) => {
                TokenService.setAccessToken(data.userAccessToken);
                TokenService.setRefreshToken(data.userRefreshToken);
                setLoggedInToNewProvider(true);
            },
        });
    };

    const UnclaimedProfileSwitchDialogElement = (
        <UnclaimedProfileSwitchDialog
            visible={switchingProvidersInProgress}
            listeningForMessages={listeningForMessages}
        />
    );

    return { switchUnclaimedProfile, UnclaimedProfileSwitchDialog: UnclaimedProfileSwitchDialogElement };
};

